import { useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Card } from './components/ui/card'
import { Button } from './components/ui/button'
import { ArrowLeft } from 'lucide-react'

import ContentBodyWrapper from 'components/custom/ContentBodyWrapper'
import { SustamizeSpinner } from '../Interpreter/components/sustamizeSpinner/sustamizeSpinner'

import { CountrySelector } from './components/entityDetail/CountrySelector'
import { VariantSelector } from './components/entityDetail/VariantSelector'
import { EmissionHeader } from './components/entityDetail/EmissionHeader'
import { EmissionSummary } from './components/entityDetail/EmissionSummary'
import { EmissionCharts } from './components/entityDetail/EmissionCharts'
import { ProcessChain } from './components/entityDetail/ProcessChain'

import {
  sagaGetPreMadeAssemblyAction,
  sagaGetSearchResultsAction,
  sagaGetVariantsAction,
} from 'saga/actions/assembler'

import { clearAssemblyResult } from 'store/slices/assemblyResultSlice'
import { clearVariants } from 'store/slices/variantSlice'

import { RootState } from 'store'
import { BuildEntityParams } from 'store/types/assembler'
import { Variant, ContextSearchResult } from 'store/types/searchResults'
import { StandardPartsInformation } from 'store/types'
import { componentPermissions } from 'config/permissions'
import { componentNames } from 'config/roles'
import { RoutePaths } from 'routes/paths'
import { Typography } from '@mui/material'

const DEFAULT_COUNTRY_CODE = 'oecd1'
const DEFAULT_VARIANT_TAG = 'var1'

const ProductDetailPage = () => {
  const { sid } = useParams<{ sid: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  // ----- URL STATE -------------------------------------------------
  // read current countryCode & variantTag from URL (fallback to defaults)
  const countryCodeFromUrl =
    searchParams.get('countryCode') || DEFAULT_COUNTRY_CODE
  const variantTagFromUrl = searchParams.get('variant') || DEFAULT_VARIANT_TAG

  // ----- REDUX STATE ------------------------------------------------
  const assemblyData = useSelector(
    (state: RootState) => state.assemblyResult.assemblyResponse?.data
  )
  const variants = useSelector(
    (state: RootState) => state.variantResult.variantResponse?.data
  )

  // 🔹 Read countries directly from Redux

  const countries: ContextSearchResult[] | undefined = useSelector(
    (state: RootState) =>
      state.searchResults.searchResultsCountries?.data.results
  )

  // we'll keep currently chosen Country object + Variant object
  // (for nice display, not for authority-of-truth)
  const [selectedCountry, setSelectedCountry] =
    useState<ContextSearchResult | null>(null)
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null)

  // 🔹 Fetch once via saga
  useEffect(() => {
    if (!sid) return
    dispatch(sagaGetSearchResultsAction('.energy.countries'))
  }, [dispatch])

  // ----- FETCH VARIANTS (depends only on sid) -----------------------
  useEffect(() => {
    if (!sid) return
    dispatch(clearVariants())
    dispatch(sagaGetVariantsAction({ sid }))
  }, [dispatch, sid])

  // ----- FETCH ASSEMBLY (depends on sid, variantTagFromUrl, countryCodeFromUrl)
  useEffect(() => {
    if (!sid) return

    dispatch(clearAssemblyResult())

    const params: BuildEntityParams = {
      variant: variantTagFromUrl,
      countryCode: countryCodeFromUrl,
      recyclingRatio: 0,
    }

    dispatch(sagaGetPreMadeAssemblyAction({ sid, p: params }))
  }, [dispatch, sid, variantTagFromUrl, countryCodeFromUrl])

  // ----- SYNC selectedVariant once variants + URL are known ----------
  useEffect(() => {
    if (!variants) return
    const match = variants.find((v) => v.variantTag === variantTagFromUrl)
    if (match) {
      setSelectedVariant(match)
    }
  }, [variants, variantTagFromUrl])

  useEffect(() => {
    if (!countries) return
    const match = countries.find(
      (c) => c.fullContext?.shortName === countryCodeFromUrl
    )
    if (match) {
      setSelectedCountry(match)
    }
  }, [countries, countryCodeFromUrl])

  const handleCountryChange = (country: ContextSearchResult) => {
    setSelectedCountry(country)

    const newCountryCode =
      country?.fullContext?.shortName || DEFAULT_COUNTRY_CODE

    // push new query params
    setSearchParams({
      countryCode: newCountryCode,
      variant: variantTagFromUrl, // keep same variant
    })
  }

  // When user picks a new variant in the dropdown:
  // - Update local selectedVariant (for label)
  // - Update URL with new variantTag
  //   which re-triggers useEffect(fetch assembly)
  const handleVariantChange = (variant: Variant) => {
    setSelectedVariant(variant)

    setSearchParams({
      countryCode: countryCodeFromUrl,
      variant: variant.variantTag,
    })
  }

  // ----- LOADING STATE ---------------------------------------------
  if (!assemblyData) {
    return (
      <ContentBodyWrapper title={'Entity detail'}>
        <div className="flex flex-col items-center justify-center py-12">
          <SustamizeSpinner />
        </div>
      </ContentBodyWrapper>
    )
  }

  const { information, result } = assemblyData
  const standardPartsInfo = information as StandardPartsInformation

  // figure out what variantTag is "active" for the selector UI
  // source of truth is URL -> variantTagFromUrl
  const activeVariantTag = variantTagFromUrl

  return (
    <ContentBodyWrapper title={'Entity detail'}>
      <div className="container mx-auto px-4 py-8 space-y-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/toolbox/standard-parts')}
          className="mb-2"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <div className="space-y-6">
          <Typography
            variant="h4"
            className="h-full px-4"
            fontSize="1.5rem"
            fontWeight="500"
          >
            Search parameters
          </Typography>

          <Card className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <CountrySelector
                countries={countries ?? []}
                value={selectedCountry?.sid}
                onChange={handleCountryChange}
              />
              <VariantSelector
                variants={variants ?? []}
                value={standardPartsInfo.general.variant}
                onChange={handleVariantChange}
              />
            </div>
          </Card>
        </div>

        <EmissionHeader
          identification={standardPartsInfo.identification}
          general={standardPartsInfo.general}
          technical={standardPartsInfo.technical}
          timestamps={standardPartsInfo.timestamps}
        />
        <EmissionSummary
          totalEmissions={result?.totalEmissions}
          emissionsByCategory={result?.emissionsByCategory}
        />

        <EmissionCharts
          emissionOriginComposition={result?.emissionOriginComposition}
          emissionsByCategory={result?.emissionsByCategory}
        />

        <ProcessChain processChain={result?.calculationProcessChain ?? []} />
      </div>
    </ContentBodyWrapper>
  )
}

const standardPartsDetailsPage = {
  routeProps: {
    path: RoutePaths.toolboxStandardPartsDetails,
    title: 'Entity detail',
    element: <ProductDetailPage />,
    roles: componentPermissions[componentNames.pageStandardParts],
    requiresAuthentication: true,
  },
  name: 'Standard Parts',
}

export default standardPartsDetailsPage
